import React from 'react';
import { Box, Card, CardContent, Typography, styled } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { ReactComponent as ChevronRightSvg } from 'assets/icons/ChevronRightIcon.svg';
import { radius } from 'themes/radius';

type BaseWidgetCardProps = {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  className?: string;
  /**
   * Override the header padding. When omitted, the default header padding
   * (theme.spacing(2) on all sides) is used unchanged.
   */
  headerSx?: SxProps<Theme>;
  /**
   * Override the content padding/margin. When omitted, the default content
   * padding (0 top, spacing(2) sides & bottom) is used unchanged.
   */
  contentSx?: SxProps<Theme>;
  /**
   * Show the header row (icon, title, chevron/action).
   * Defaults to true — omitting it preserves default behavior.
   */
  showHeader?: boolean;
};

// showChevron implies the card is clickable, onClick is required.
type WithChevron = BaseWidgetCardProps & {
  showChevron: true;
  onClick: () => void;
  headerAction?: never;
};

// when showChevron is absent, headerAction is allowed and onClick is optional
type WithoutChevron = BaseWidgetCardProps & {
  showChevron?: false;
  onClick?: () => void;
  headerAction?: React.ReactNode;
};

type WidgetCardProps = WithChevron | WithoutChevron;

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  isolation: 'isolate',
  borderRadius: radius.card,
  boxShadow: `0 1px 4px 0 ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.background.paper,
  width: '100%',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  '&.clickable': {
    cursor: 'pointer',
  },
  '&.clickable::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

// Icon wrapper: 20 px on mobile, 24 px on tablet+.
const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  color: theme.palette.primary.main,
  fontSize: '20px',
  '& .MuiSvgIcon-root, & svg': {
    fontSize: 'inherit',
    width: '1em',
    height: '1em',
  },
  paddingLeft: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    fontSize: '24px',
    paddingLeft: theme.spacing(2),
  },
}));

// 16 px on mobile, title3 20 px on tablet
const CardTitle = styled(Typography)(({ theme }) => ({
  flex: 1,
  ...theme.typography.callout,
  color: theme.palette.grey[800],
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title3,
  },
})) as typeof Typography;

const HeaderActions = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
}));

const ActionSlot = styled(Box)(() => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
}));

const ChevronIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: '16px',
  '& .MuiSvgIcon-root, & svg': {
    fontSize: 'inherit',
    width: '1em',
    height: '1em',
  },
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
    paddingRight: theme.spacing(2),
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}));

export const WidgetCard = ({
  icon,
  title,
  headerAction,
  showChevron = false,
  onClick,
  children,
  className,
  headerSx,
  contentSx,
  showHeader = true,
}: WidgetCardProps) => {
  const hasHeaderRight = headerAction != null || showChevron;

  const handleKeyDown = onClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <StyledCard className={className}>
      {showHeader && (
        <CardHeader sx={headerSx}>
          <HeaderContent
            className={onClick ? 'clickable' : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
          >
            <IconWrapper>{icon}</IconWrapper>
            <CardTitle component="h3">{title}</CardTitle>
          </HeaderContent>
          {hasHeaderRight && (
            <HeaderActions>
              {headerAction != null && <ActionSlot>{headerAction}</ActionSlot>}
              {showChevron && (
                <ChevronIcon>
                  <SvgIcon component={ChevronRightSvg} aria-hidden="true" focusable="false" />
                </ChevronIcon>
              )}
            </HeaderActions>
          )}
        </CardHeader>
      )}

      {children != null && <StyledCardContent sx={contentSx}>{children}</StyledCardContent>}
    </StyledCard>
  );
};
